/**
 * Trap Station map composition.
 *
 * The Stations surface (plan §4.2) draws the district's stations from the
 * `trap-station` vector tiles, over the parcels and the district boundary. The
 * facts that view needs are pure functions of what the tile and the click hand
 * it, so they live here — unit-testable without a browser — rather than inside
 * the map component:
 *
 * - the tile URL template the session's tegola base composes;
 * - the parcel a click hit, read from the parcel tile's own properties;
 * - the station a tile feature names, read from its property or its feature id;
 * - the station layer's filter, which is how the "show retired" toggle works:
 *   retired stations are part of the same tile, so showing or hiding them is a
 *   filter change and never a re-fetch (plan §3.10).
 *
 * Nothing here talks to MapLibre: the arrays it returns are filter expressions,
 * and the components hand them to the layers' `filter` props.
 */

/** A click's parcel hit: the county parcel the clicked point is on, as the
 * parcel tile names it. Both halves are needed — APNs are not unique across
 * counties, so `region` is what disambiguates the resolve call (plan §3.3). */
export interface ParcelHit {
	external_id: string;
	region: string;
}

/** A parcel click as the map reports it: the parcel the clicked point is on
 * (empty when the click hit no parcel — below the parcels' z14, or a gap in
 * coverage), and the point itself, which is where a station would stand. */
export interface StationParcelClick extends ParcelHit {
	lng: number;
	lat: number;
}

/** A rendered MVT feature, as much of it as these helpers read: MapLibre hands
 * back `properties` (possibly absent) and the feature id. */
export interface TileFeature {
	id?: unknown;
	properties?: Record<string, unknown> | null;
}

/**
 * The Trap Station tile set's URL template, for the session's absolute tegola
 * base (`session.urls.tegola`, which ends in `/api/`). An absent base yields no
 * tiles at all, so the source is left unmounted rather than pointed at a
 * relative URL MapLibre would reject. The organization is injected by the
 * server's own session, never by the client.
 */
export function stationTilesURL(tegolaBase: string | undefined): string[] {
	if (!tegolaBase) return [];
	return [`${tegolaBase}maps/trap-station/{z}/{x}/{y}`];
}

/**
 * The parcel a map click hit, read from the rendered parcel features (the
 * `parcel-fill` layer). The first feature carrying BOTH an `external_id` and a
 * `region` wins: an APN without its county cannot be resolved unambiguously, so
 * a tile that has not been extended with `region` reads as no hit at all rather
 * than as a hit the resolve call might answer for the wrong county.
 */
export function parcelHitFromFeatures(
	features: Array<{ properties?: Record<string, unknown> | null }>,
): ParcelHit | null {
	for (const feature of features) {
		const externalID = feature.properties?.external_id;
		const region = feature.properties?.region;
		if (
			typeof externalID === "string" &&
			externalID !== "" &&
			typeof region === "string" &&
			region !== ""
		) {
			return { external_id: externalID, region };
		}
	}
	return null;
}

/**
 * The station id a rendered station feature names. The tile carries `id` as a
 * property (the layer's `id_fieldname`), and the feature id is the same number;
 * the property is read first because it survives any re-keying of the feature
 * id, and the feature id is the fallback. An MVT encoder may carry the number
 * as its decimal text, and that names the same station. Null when neither names
 * an integer, so a click on something that is not a station is never mistaken
 * for station 0.
 */
export function stationFeatureID(feature: TileFeature): number | null {
	for (const value of [feature.properties?.id, feature.id]) {
		if (typeof value === "number" && Number.isInteger(value)) return value;
		if (typeof value === "string" && value.trim() !== "") {
			const parsed = Number(value);
			if (Number.isInteger(parsed)) return parsed;
		}
	}
	return null;
}

/** `active` read as a boolean, whatever the tile encoded it as: the layer
 * selects the column as it stands, so a boolean tile and an integer tile (the
 * Feature layer's `active::int` convention) both answer here. */
const ACTIVE: unknown[] = ["to-boolean", ["get", "active"]];

/**
 * The station layer's display filter. Retired stations are never deleted and
 * are always in the tile (plan §3.10), so "show retired" is this filter:
 *
 * - `showRetired` false → the active stations only;
 * - `showRetired` true  → active and retired alike.
 *
 * Selection is deliberately not part of it: the selected station is emphasised
 * where it is drawn, and a filter naming an id would hide every other station.
 */
export function stationCircleFilter(opts: { showRetired: boolean }): unknown[] {
	if (!opts.showRetired) return ["==", ACTIVE, true];
	return ["any", ["==", ACTIVE, true], ["==", ACTIVE, false]];
}

/**
 * A station's identity as the operator reads it (plan §3.6): the id and the
 * address of the Site it stands on. A station whose Site carries no address (an
 * imported row the fabric has not reconciled yet) still reads as its id.
 */
export function stationLabel(id: number, site: string): string {
	return site ? `Station #${id} — ${site}` : `Station #${id}`;
}
